import { Log, LogGroup, Millis, Point, Rect, Size, now } from "../.././MyHelpers";
import { ResourceIdentifier } from "../../ResourceManager/ResourceIdentifier";
import { ISprite, SpriteUpdateState } from "./ISprite";


export interface  SpriteProperties
{
	width: number,
	height: number,
	fps: number
}

export interface  SpriteConfig
{
	props: SpriteProperties,
	frames: Point[]	
}

export enum SpriteFramePosition
{
	First,
	Last
}

export type OnSpriteCompleteCallback = () => void;
export type OnSpriteStartCallback = () => void;
export type GenericSpriteCallback = () => void;
export type OnSpriteDrawFrameCallback = () => void;

export class SpriteSharedData
{
	public image: HTMLImageElement = null;
	public decodedFrames: ImageBitmap[] = [];
	public decoded: boolean = false;
}

export class Sprite extends ISprite
{

	public image: HTMLImageElement = null;
	public config: SpriteConfig = null;

	private frameIndex: number = 0;
	private newFrameIndex: number = 0;

	private isPlaying: boolean = false;
	private frameChanged: boolean = false;
	private started: Millis = 0;
	private frameDuration: Millis = 0;

	private logGroup: LogGroup = new LogGroup("Sprite");
	private repeat: boolean = true;

	private resid:ResourceIdentifier = null;

	private size: Size = null;
	private genericRectangle: Rect = null;

	protected frameResetToStart: boolean = false;

	// expected duration based on number of frames and fps, in millis
	private animDuration: number = 0;
	public ResetToFirstFrame: boolean = true;

	public get CurrentFrame(): ImageBitmap
	{
		return this.shared.decodedFrames[this.frameIndex]
	}

	readonly currentFrameRect: Rect;
	public get CurrentFrameRect(): Rect
	{
		return this.currentFrameRect;
	}

	public get CurrentFrameIndex(): number
	{
		return this.frameIndex;
	}




	public get IdleFrame(): ImageBitmap
	{
		return this.shared.decodedFrames[0];
	}

	public get IdleFrameRect(): Rect
	{
		return new Rect(
			0,
			0,
			this.config.props.width,
			this.config.props.height
		);
	}

	public get IdleFrameIndex(): number
	{
		return 0;
	}

	
	public override NumFrames(): number {
		return this.shared.decodedFrames.length;
	}
	public override GetDecodedFrame(frameIndex: number): ImageBitmap {
		return this.shared.decodedFrames[frameIndex]
	}
	public override GetDecodedFrameRect(frameIndex: number): Rect {
		return new Rect(
			0,
			0,
			this.config.props.width,
			this.config.props.height
		);	
	}





	public get ResourceIdentifier(): ResourceIdentifier
	{
		return this.resid;
	}
	
	public get LogGroup(): LogGroup
	{
		return this.logGroup;
	}

	
	public get Duration(): number
	{
		return this.animDuration;
	}

	public shared: SpriteSharedData = new SpriteSharedData();

	protected path: string;

	protected constructor(srcImage: HTMLImageElement, config: SpriteConfig, path: string, resid: ResourceIdentifier = null, logGroup: LogGroup = null, shared: SpriteSharedData = null) {
		
		super();

		this.image = srcImage;
		this.config = config;
		this.path = path;
		this.resid = resid;

		if (logGroup != null)
			this.logGroup = logGroup;

		this.frameDuration = 1000 / config.props.fps;

		this.animDuration = this.config.frames.length / this.config.props.fps * 1000;

		this.frameIndex = config.frames.length -1;

		this.size = new Size(this.config.props.width, this.config.props.height);
		this.genericRectangle = new Rect(0, 0, this.size.Width, this.size.Height);

		if (shared != null) this.shared = shared;

		this.currentFrameRect = new Rect(
			0,
			0,
			this.config.props.width,
			this.config.props.height
		);
	}

	public Running(): boolean
	{
		return this.isPlaying;
	}

	public static Create(srcImage: HTMLImageElement, config: SpriteConfig, path: string, resid: ResourceIdentifier = null, logGroup: LogGroup = null, shared: SpriteSharedData = null): Sprite
	{
		return new Sprite(srcImage, config, path, resid, logGroup, shared);
	}

	public Reset(): void {
		this.newFrameIndex = 0;
	}

	public Name():string
	{
		return this.resid.ID();
	}	

	public Start(repeat: boolean = true): void {
		this.isPlaying = true;
		this.started = now();

		this.repeat = repeat;
	}

	public Stop(): void {
		this.isPlaying = false;
	}

	public StopAndReset(): void {
		this.Stop();
		this.Reset();
	}

	public Restart(repeat: boolean = true): void {
		this.Reset();
		this.Start(repeat);
	}

	public ToLastFrame(): void {
		this.newFrameIndex = this.config.frames.length -1;
	}

	public SetFPS(fps: number): void {
		this.frameDuration = 1000 / fps;
	}

    public GetFPS(): number
    {
        return Math.roundTo(this.frameDuration / 1000, 2);
    }

	public Update(millis = null): SpriteUpdateState {

		if (this.isPlaying)
		{
			if (millis == null) millis = now();

			let elapsed = millis - this.started;
			this.newFrameIndex = Math.floor(elapsed / this.frameDuration) % this.config.frames.length;

			//// Log.Debug("SPRITE", "new index " + this.newFrameIndex);
			
			// cap frame index to the last, if animation is not repeated, and stop
			if (!this.repeat && elapsed >= (this.animDuration + this.frameDuration)) {

				if (this.ResetToFirstFrame)
				{
					this.newFrameIndex = 0;
				}
				else
				{
					this.newFrameIndex = this.config.frames.length -1;
				}


				this.isPlaying = false;

				this.OnSpriteComplete();

				return SpriteUpdateState.Finished;
			}
		}

		// notify if frame index changes
		if (this.newFrameIndex != this.frameIndex) {

			// need to refresh image!
			this.frameIndex = this.newFrameIndex;
			this.frameChanged = true;

			return SpriteUpdateState.FrameChanged;
		}

		if (!this.isPlaying)
		{
			return SpriteUpdateState.Idle;
		}

		return SpriteUpdateState.NoChange;
	}

	public Repeat(repeat: boolean)
	{
		this.repeat = repeat;
		if (this.repeat == false)
		{
			let n = now();
			let elapsed = n - this.started;
			this.started += Math.floor(elapsed / (this.frameDuration * this.config.frames.length)) * (this.frameDuration * this.config.frames.length) ;
		}
	}

	// returns rect of given frame index
	public RectOfFrame(frameIndex: number): Rect {

		let rect: Rect = null;
		if (frameIndex >= 0 && frameIndex < this.config.frames.length) {
			rect = new Rect(
				this.config.frames[frameIndex].x,
				this.config.frames[frameIndex].y,
				this.config.props.width,
				this.config.props.height,
			);
		}
		else {
			Log.Error(this.logGroup, "Frame index " + frameIndex + " does not exist")
		}

		return rect;
	}

	public get GenericRectangle() 
	{
		return this.genericRectangle;
	}

	// checks and resets frame changed flag
	public FrameChanged(): boolean {
		let result: boolean = false;

		if (this.isPlaying || !this.frameResetToStart) {
			result = this.frameChanged;
		}

		this.frameResetToStart = true;
		this.frameChanged = false;
		
		return result;
	}

	public Size(): Size {
		return this.size;
	}

	// shallow copy, keep the same instance of image and json config
	public Copy(): Sprite {
		return new Sprite(this.image.cloneNode() as HTMLImageElement, this.config, this.path, this.resid, this.logGroup, this.shared);
	}




	protected numDecodedFrames: number = 0;
	protected numErrorFrames: number = 0;
	protected numProcessedFrames: number = 0;

	protected decodeStartTime: number = 0;

	protected decodedFramesSize: number = 0;

	public Decode(): void
	{
		if (this.shared.decodedFrames.length != 0 || this.shared.decoded)
		{
			Log.Error(this.logGroup, "Frames already decoded, skipping...");
			debugger;
			return;
		}

		this.shared.decoded = true;
		this.decodeStartTime = now();


		let twoStepDecoding: boolean = true;

		if (twoStepDecoding)
		{
			//// Log.Debug(this.logGroup, "Decoding whole image")
			createImageBitmap(this.image).then((decodedImage: ImageBitmap) => {
				let decodeWholeImage = now();

				//// Log.Debug(this.logGroup, "Decoded image size: " + formatBytes(decodedImage.width * decodedImage.height * 4));

				//// Log.Debug(this.logGroup, "Decoding whole image took " + ((decodeWholeImage - this.decodeStartTime) / 1000).toFixed(2) + "s");

				this.config.frames.forEach((point: Point, index: number) => {
				
					//// Log.Debug(this.logGroup, "Decoding frame " + index);
					createImageBitmap(
						decodedImage, 
						this.config.frames[index].x,
						this.config.frames[index].y,
						this.config.props.width,
						this.config.props.height
					).then((decodedFrame: ImageBitmap) => {

						this.shared.decodedFrames[index] = decodedFrame;
						this.FrameProcessed(index, true);
					}).catch((reason: any) => {
						this.FrameProcessed(index, false);
					});
				});
			}).catch((reason: any) => {
                Log.Error(this.logGroup, "Decoding whole image failed");
                Log.Critical(this.logGroup, `Error decoding sprite: ${JSON.stringify(reason)}`);
			});
		}
		else
		{
			// decode only frames without whole image
			this.config.frames.forEach((point: Point, index: number) => {
				
				//// Log.Debug(this.logGroup, "Decoding frame " + index);
				createImageBitmap(
					this.image, 
					this.config.frames[index].x,
					this.config.frames[index].y,
					this.config.props.width,
					this.config.props.height
				).then((decodedFrame: ImageBitmap) => {

					this.shared.decodedFrames[index] = decodedFrame;
					this.FrameProcessed(index, true);
				}).catch((reason: any) => {
					this.FrameProcessed(index, false);
				});
			});
		}
	}

	protected FrameProcessed(frameIndex: number, success: boolean)
	{


		this.decodedFramesSize += this.config.props.width * this.config.props.height * 4;

		if (success)
		{
			this.numDecodedFrames++;
		}
		else
		{
			this.numErrorFrames++;
		}

		this.numProcessedFrames++;
		if (this.numProcessedFrames == this.config.frames.length)
		{
			let decodeEndTime = now();

			//// Log.Debug(this.logGroup, "Decode took " + ((decodeEndTime - this.decodeStartTime) / 1000).toFixed(2) + "s");
			//// Log.Debug(this.logGroup, "Decoded sprite size: " + formatBytes(this.decodedFramesSize) + " (" + this.path + ")");

			if (this.numErrorFrames == 0)
			{
				//// Log.Debug(this.logGroup, "Processed all " + this.config.frames.length + " frames, finished decoding");
				this.FramesDecoded();
			}
			else
			{
				//// Log.Debug(this.logGroup, "Processed all frames, error decoding");
				this.FramesDecodeError();
			}
		}
	}

	protected FrameDecoded(decodedFrame: ImageBitmap, frameIndex: number)
	{
		//// Log.Debug(this.logGroup, "Decoded frame " + frameIndex);
		
		this.numDecodedFrames++;
		if (this.numDecodedFrames == this.config.frames.length)
		{
			//// Log.Debug(this.logGroup, "Decoded all frames, finished decoding");
			this.FramesDecoded();
		}
	}

	public Clean(): void
	{
		this.numProcessedFrames = 0;
		this.numDecodedFrames = 0;
		this.numErrorFrames = 0;

		this.shared.decodedFrames = [];

		// Log.Debug(this.logGroup, "Cleanup finished");
		this.FramesCleaned();
	}
}
